
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useMutation } from "@apollo/client";
import { SEND_MAGIC_LINK } from "@/integrations/apollo/operations";

const Login = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [sendMagicLink] = useMutation(SEND_MAGIC_LINK);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Email required", {
        description: "Please enter your email address"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Call the GraphQL mutation directly using Apollo Client
      const response = await sendMagicLink({
        variables: { email }
      });

      if (response.errors) {
        throw new Error(response.errors[0].message || "Failed to send magic link");
      }

      toast.success("Magic link sent!", {
        description: "Check your email for the login link"
      });

    } catch (error) {
      console.error("Error sending magic link:", error);
      toast.error("Error", {
        description: error.message || "Failed to send magic link. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-black/40 backdrop-blur-sm rounded-lg p-8 shadow-lg border border-white/10">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Welcome Back</h1>
          <p className="text-white/80">Enter your email to sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 h-5 w-5" />
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-white/10 text-white border-white/20 focus-visible:ring-primary/50"
                disabled={isLoading}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-white text-primary hover:bg-white/90"
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send Magic Link"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-white/60 text-sm">
            Don't have an account?{" "}
            <Button
              variant="link"
              className="text-white p-0 h-auto font-normal"
              onClick={() => navigate("/")}
            >
              Get started
            </Button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
